import {
  AnalyticsBrowser,
  type MiddlewareFunction,
} from "@segment/analytics-next";
import { EVENT_FORMATTER, PropertiesPlugin } from "./segmentPlugins";
import type { AnalyticsEvent, LegacyEventData, LegacyPageData } from "./types";

const screenDimensionsMiddleware: MiddlewareFunction = ({ payload, next }) => {
  const { width, height } = window.screen;
  const { devicePixelRatio } = window;

  payload.obj.context ??= {};
  payload.obj.context.screen = { width, height, density: devicePixelRatio };

  // Pass the enriched payload to the next middleware or destination
  next(payload);
};

let analyticsError: Error | undefined;

function loadAnalytics(): AnalyticsBrowser {
  const analyticsBrowser = new AnalyticsBrowser();

  // calling .load() on the analytics browser will throw an error if the browser is not available
  if (typeof window === "undefined") return analyticsBrowser;

  analyticsBrowser
    .load({
      writeKey: process.env.NEXT_PUBLIC_SEGMENT_KEY ?? "",
    })
    .catch((err) => {
      analyticsError = err;
      console.error(
        "failed to initialise analytics, possibly browser privacy settings",
        err,
      );
    });

  return analyticsBrowser;
}

// getUser wraps the .user() call, returning an error if the analytics browser failed to initialise
export function getUser(analyticsBrowser: AnalyticsBrowser) {
  if (analyticsError) return Promise.reject(analyticsError);
  return analyticsBrowser.user();
}

export const analytics = loadAnalytics();
export const propertiesPlugin = new PropertiesPlugin();
analytics.register(propertiesPlugin);
analytics.register(EVENT_FORMATTER);
analytics.addSourceMiddleware(screenDimensionsMiddleware);

function capitalize(string?: string): string {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function pageLegacyEvent(eventData: LegacyPageData) {
  analytics.page(
    `PassportDashboard${eventData.userJourney}${eventData.screen}${eventData.action ?? ""}`,
    {
      userJourney: eventData.userJourney,
      screen: eventData.screen,
      action: eventData.action,
      ...eventData.extras,
    },
  );
}

// Do not create new events with this function. Use trackEvent instead.
export function trackLegacyEvent(
  eventData: LegacyEventData,
  product: "PassportDashboard" | "Play" | "" = "PassportDashboard",
) {
  let controlAndType = "";
  const extras: Record<string, unknown> = { ...eventData.extras };
  if ("control" in eventData && "controlType" in eventData) {
    extras.control = eventData.control;
    extras.controlType = eventData.controlType;
    controlAndType =
      capitalize(eventData.control) + capitalize(eventData.controlType);
  }
  analytics.track(
    `${product}${eventData.userJourney}${eventData.screen}_${controlAndType}${eventData.action ?? ""}`,
    {
      userJourney: eventData.userJourney,
      screen: eventData.screen,
      action: eventData.action,
      ...extras,
    },
  );
}

export function trackEvent(data: AnalyticsEvent) {
  let event = "";
  if ("component" in data) {
    event = `Play_${data.component}_${data.action}`;
  } else if ("product" in data) {
    event = `Play_${data.product}_${data.action}`;
  } else if ("page" in data) {
    event = `Play_${data.page}`;
  }
  analytics.track(event, data.extras);
}
